import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import { useBatchMode } from "../contexts/BatchModeContext";
import { resolveImageUrl } from "../lib/api";
import StockBadge from "./StockBadge";
import ImageLightbox from "./ImageLightbox";

export default function ProductCard({ product, index = 0 }) {
  const { mode } = useBatchMode();
  const navigate = useNavigate();
  const [zoomOpen, setZoomOpen] = useState(false);

  const wholesaleAvailable =
    (product.wholesale_packages && product.wholesale_packages.length > 0) ||
    product.wholesale_price != null;
  const showWholesale = mode === "wholesale" && wholesaleAvailable;
  const soldOut = product.stock_status === "out_of_stock";
  let optionsLabel = "Choose options";
  if (soldOut) optionsLabel = "Out of stock";
  else if (mode === "wholesale" && !wholesaleAvailable) optionsLabel = "No wholesale";

  // Lowest available price for the selected mode (for "from $X" display)
  let priceLabel = "";
  let priceHint = "";
  if (showWholesale) {
    const pkgs = product.wholesale_packages || [];
    if (pkgs.length > 0) {
      const min = Math.min(...pkgs.map((p) => p.price));
      priceLabel = `From $${min.toFixed(0)}`;
      priceHint = `${pkgs.length} sizes`;
    } else if (product.wholesale_price != null) {
      priceLabel = `$${product.wholesale_price.toFixed(0)}`;
      priceHint = `min ${product.wholesale_min_qty || 5}`;
    }
  } else {
    const pkgs = product.small_batch_packages || [];
    if (pkgs.length > 0) {
      const min = Math.min(...pkgs.map((p) => p.price));
      priceLabel = `From $${min.toFixed(0)}`;
      priceHint = `${pkgs.length} sizes`;
    } else {
      priceLabel = `$${product.price?.toFixed(0)}`;
    }
  }

  const imgUrl = resolveImageUrl(product.image);

  const openZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoomOpen(true);
  };

  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
      className="group relative border border-white/10 hover:border-[var(--hk-purple)]/60 bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden transition-all hover:-translate-y-1"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="aspect-[4/5] overflow-hidden bg-black relative">
          <img
            src={imgUrl}
            alt={product.name}
            className={`w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ${soldOut ? "grayscale opacity-40" : ""}`}
          />
          {imgUrl && (
            <button
              type="button"
              onClick={openZoom}
              aria-label="View full-size image"
              data-testid={`zoom-btn-${product.id}`}
              className="absolute bottom-2.5 right-2.5 w-11 h-11 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/70 backdrop-blur border border-white/25 text-white hover:bg-[var(--hk-orange)] hover:text-black active:scale-95 transition-all shadow-[0_4px_18px_rgba(0,0,0,0.55)] touch-manipulation"
            >
              <Maximize2 size={16} />
            </button>
          )}
        </div>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <span className="bg-black/70 backdrop-blur text-[10px] font-mono-alt text-[var(--hk-purple-text)] tracking-widest uppercase px-2 py-1">
            {product.category}
          </span>
          {showWholesale && (
            <span className="bg-[var(--hk-purple)] text-white text-[10px] font-mono-alt tracking-widest uppercase px-2 py-1">
              Wholesale
            </span>
          )}
          <StockBadge status={product.stock_status} productId={product.id} />
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="font-display text-lg uppercase tracking-tight text-white line-clamp-1">
              {product.name}
            </h3>
            <span className="font-display text-base text-[var(--hk-orange)] whitespace-nowrap">
              {priceLabel}
            </span>
          </div>
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-4">
            {product.strain}{priceHint ? ` · ${priceHint}` : ""}
          </p>
        </div>
      </Link>
      <div className="px-5 pb-5">
        <button
          data-testid={`choose-options-${product.id}`}
          onClick={() => navigate(`/product/${product.id}`)}
          disabled={soldOut || (mode === "wholesale" && !wholesaleAvailable)}
          className="w-full border border-white/20 hover:border-[var(--hk-orange)] hover:bg-[var(--hk-orange)] hover:text-black py-2.5 font-display text-xs uppercase tracking-[0.2em] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {optionsLabel}
        </button>
      </div>
      <ImageLightbox
        open={zoomOpen}
        src={imgUrl}
        alt={product.name}
        onClose={() => setZoomOpen(false)}
      />
    </motion.div>
  );
}
